import clsx from 'clsx';
import React from 'react';

import classes from './Tabs.module.scss';

interface TabsProps<T> {
    tabs: Array<{
        label: string;
        value: T;
    }>;
    selectedTab: T;
    className?: string;
    onChange: (tab: T) => void;
}

const calculateIndicatorAttributes = (element: HTMLButtonElement) => {
    return {
        left: element.offsetLeft,
        width: element.offsetWidth
    };
};

export const Tabs = <T,>(props: TabsProps<T>): JSX.Element => {
    const rootRef = React.useRef<HTMLDivElement>(null);

    const [indicatorAttributes, setIndicatorAttributes] = React.useState<React.CSSProperties>({});

    React.useEffect(() => {
        const tabElement = rootRef.current?.querySelector(
            `[data-tab-value="${props.selectedTab}"]`
        ) as HTMLButtonElement;
        if (!tabElement) return;

        setIndicatorAttributes(calculateIndicatorAttributes(tabElement));
    }, [props.tabs, props.selectedTab]);

    React.useEffect(() => {
        const resizeHandler = () => {
            setIndicatorAttributes(
                calculateIndicatorAttributes(
                    rootRef.current?.querySelector(
                        `[data-tab-value="${props.selectedTab}"]`
                    ) as HTMLButtonElement
                )
            );
        };

        window.addEventListener('resize', resizeHandler);

        return () => window.removeEventListener('resize', resizeHandler);
        // eslint-disable-next-line
    }, [props.selectedTab]);

    const changeHandler = (tab: T) => {
        if (props.selectedTab === tab) return;
        props.onChange(tab);
    };

    return (
        <div ref={rootRef} className={clsx(classes.root, props.className)}>
            {props.tabs.map(tab => (
                <button
                    key={String(tab.value)}
                    className={clsx(classes.tab, {
                        [classes.tabActive]: props.selectedTab === tab.value
                    })}
                    data-tab-value={tab.value}
                    onClick={() => changeHandler(tab.value)}
                >
                    {tab.label}
                </button>
            ))}

            <div className={classes.indicator} style={indicatorAttributes} />
        </div>
    );
};
